import { EMessengerChatSourceType, EMessengerScreenName, type IMessengerChatSource } from '@xometry/ui';
import { ESideOverlayType, type TSideOverlayContext } from 'stores/SideOverlay/SideOverlayStore';

/**
 * Document threads on a deal page that follow URL while Message Center stays open.
 * RFQ_OFFER is intentionally excluded: offers are rows in a table on the RFQ page and have no
 * dedicated URL, so `buildDealMessengerChatSource` can only resolve the parent RFQ from the URL —
 * overriding an explicit RFQ_OFFER button click with the RFQ source.
 */
const DEAL_URL_FOLLOWING_SOURCE_TYPES = new Set<EMessengerChatSourceType>([
  EMessengerChatSourceType.ORDER_CONFIRMATION,
  EMessengerChatSourceType.PAYMENT,
  EMessengerChatSourceType.PART,
  EMessengerChatSourceType.ENQUIRY,
  EMessengerChatSourceType.PROVIDER_ORDER,
  EMessengerChatSourceType.OUTCOMING_INVOICE,
  EMessengerChatSourceType.INCOMING_INVOICE,
  EMessengerChatSourceType.QUALITY_CONTROL,
  EMessengerChatSourceType.SHIPPING,
  EMessengerChatSourceType.STORAGE,
  EMessengerChatSourceType.CLAIM,
  EMessengerChatSourceType.JOB,
  EMessengerChatSourceType.RFQ,
  EMessengerChatSourceType.PAYOUT,
]);

export const getMessengerOverlayChatSource = (context: TSideOverlayContext): IMessengerChatSource | null => {
  if (context.type !== ESideOverlayType.Messenger) return null;

  if (context.initialScreen?.name !== EMessengerScreenName.CHAT) return null;

  return context.initialScreen.params?.chatSource ?? null;
};

/**
 * Whether deal-route URL may drive the open MC thread (compact column + `liveDealThreadChatSource`).
 * False for deal-wide "all messages", person (CUSTOMER), and partner (PARTNER) — user picked those explicitly.
 */
export const shouldSyncMessengerOverlayWithDealUrl = (context: TSideOverlayContext): boolean => {
  const chatSource = getMessengerOverlayChatSource(context);

  if (!chatSource) return false;

  if (chatSource.sourceType === EMessengerChatSourceType.DEAL) return false;

  if (chatSource.sourceType === EMessengerChatSourceType.CUSTOMER) return false;

  if (chatSource.sourceType === EMessengerChatSourceType.PARTNER) return false;

  return DEAL_URL_FOLLOWING_SOURCE_TYPES.has(chatSource.sourceType);
};
